import { useEffect, useRef, useState, useCallback } from 'react';

interface UseScrollAnimationOptions {
  /** Threshold for intersection (0-1). Default: 0.1 */
  threshold?: number;
  /** Root margin for intersection observer. Default: '0px' */
  rootMargin?: string;
  /** Whether to trigger animation only once. Default: true */
  triggerOnce?: boolean;
  /** Delay before animation starts (ms). Default: 0 */
  delay?: number;
}

interface UseScrollAnimationReturn<T extends HTMLElement> {
  /** Ref to attach to the element */
  ref: React.RefObject<T>;
  /** Whether the element is visible/animated */
  isVisible: boolean;
  /** CSS classes to apply for animation */
  animationClasses: string;
}

/**
 * Hook for scroll-triggered animations using Intersection Observer.
 *
 * @example
 * ```tsx
 * function FeatureCard({ title, description }) {
 *   const { ref, animationClasses } = useScrollAnimation<HTMLDivElement>();
 *
 *   return (
 *     <div ref={ref} className={animationClasses}>
 *       <h3>{title}</h3>
 *       <p>{description}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollAnimationOptions = {}
): UseScrollAnimationReturn<T> {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
    delay = 0,
  } = options;

  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Check for reduced motion preference
  const prefersReducedMotion = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    // If user prefers reduced motion, show immediately
    if (prefersReducedMotion()) {
      setIsVisible(true);
      setHasTriggered(true);
      return;
    }

    const element = ref.current;
    if (!element) return;

    // If already triggered and triggerOnce is true, don't observe
    if (triggerOnce && hasTriggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay > 0) {
            setTimeout(() => {
              setIsVisible(true);
              setHasTriggered(true);
            }, delay);
          } else {
            setIsVisible(true);
            setHasTriggered(true);
          }

          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, delay, hasTriggered, prefersReducedMotion]);

  // Generate animation classes
  const animationClasses = isVisible
    ? 'animate-on-scroll visible'
    : 'animate-on-scroll';

  return {
    ref,
    isVisible,
    animationClasses,
  };
}

/**
 * Hook for staggered scroll animations on multiple children.
 *
 * @example
 * ```tsx
 * function FeatureList({ features }) {
 *   const { containerRef, getItemProps } = useStaggeredScrollAnimation();
 *
 *   return (
 *     <div ref={containerRef}>
 *       {features.map((feature, index) => (
 *         <div key={feature.id} {...getItemProps(index)}>
 *           {feature.title}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useStaggeredScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollAnimationOptions & { staggerDelay?: number } = {}
) {
  const { staggerDelay = 100, ...scrollOptions } = options;
  const { ref: containerRef, isVisible } = useScrollAnimation<T>(scrollOptions);

  const getItemProps = useCallback(
    (index: number) => ({
      className: `animate-on-scroll ${isVisible ? 'visible' : ''}`,
      style: {
        transitionDelay: isVisible ? `${index * staggerDelay}ms` : '0ms',
      },
    }),
    [isVisible, staggerDelay]
  );

  return {
    containerRef,
    isVisible,
    getItemProps,
  };
}

export default useScrollAnimation;
